/**
 * Lead created-at period helpers (list grouping).
 */

import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  startOfWeek,
  subWeeks,
  isAfter,
  isValid,
  parseISO,
} from "date-fns";

export const CREATED_PERIOD_ORDER = Object.freeze([
  "today",
  "yesterday",
  "this_week",
  "last_week",
  "older",
  "unknown",
]);

const PERIOD_LABELS = Object.freeze({
  today: "Today",
  yesterday: "Yesterday",
  this_week: "Earlier this week",
  last_week: "Last week",
  older: "Older",
  unknown: "Unknown date",
});

/**
 * @param {string} period
 * @returns {string}
 */
export function getCreatedPeriodLabel(period) {
  return PERIOD_LABELS[period] || PERIOD_LABELS.unknown;
}

/**
 * @param {Object} lead
 * @returns {Date|null}
 */
export function getLeadCreatedDate(lead) {
  const raw = lead?.createdAt || lead?.created_at;
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  if (!isValid(date)) {
    if (typeof raw === "string") {
      const parsed = parseISO(raw);
      return isValid(parsed) ? parsed : null;
    }
    return null;
  }
  return date;
}

/**
 * @param {Object} lead
 * @param {Date} [now]
 * @returns {"today"|"yesterday"|"this_week"|"last_week"|"older"|"unknown"}
 */
export function getLeadCreatedPeriod(lead, now = new Date()) {
  const date = getLeadCreatedDate(lead);
  if (!date) return "unknown";

  if (isToday(date)) return "today";
  if (isYesterday(date)) return "yesterday";

  // date-fns isThisWeek uses locale week; pin Monday start for SA ops
  if (isThisWeek(date, { weekStartsOn: 1 })) return "this_week";

  const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
  const lastWeekStart = subWeeks(thisWeekStart, 1);
  if (isAfter(date, lastWeekStart) || date.getTime() === lastWeekStart.getTime()) {
    // within last week window and not this week
    if (date < thisWeekStart) return "last_week";
  }

  return "older";
}

/**
 * Compact list cell: absolute timestamp for table (mock style),
 * plus relative helper for detail.
 */
export function formatLeadCreatedDisplay(lead, now = new Date()) {
  const date = getLeadCreatedDate(lead);
  if (!date) return { primary: "—", secondary: null };

  const period = getLeadCreatedPeriod(lead, now);
  const time = format(date, "HH:mm");
  const full = format(date, "MMM dd, yyyy HH:mm");
  const short = format(date, "MMM dd, HH:mm");

  if (period === "today") {
    return { primary: short, secondary: `Today ${time}`, absolute: full };
  }
  if (period === "yesterday") {
    return { primary: short, secondary: `Yesterday ${time}`, absolute: full };
  }
  return { primary: short, secondary: full, absolute: full };
}

/**
 * @param {Object[]} leads
 * @param {Date} [now]
 * @returns {Array<{ period: string, label: string, leads: Object[] }>}
 */
export function groupLeadsByCreatedPeriod(leads = [], now = new Date()) {
  const buckets = Object.fromEntries(
    CREATED_PERIOD_ORDER.map((p) => [p, []])
  );

  for (const lead of leads) {
    const period = getLeadCreatedPeriod(lead, now);
    buckets[period].push(lead);
  }

  for (const period of CREATED_PERIOD_ORDER) {
    buckets[period].sort((a, b) => {
      const da = getLeadCreatedDate(a)?.getTime() || 0;
      const db = getLeadCreatedDate(b)?.getTime() || 0;
      return db - da;
    });
  }

  return CREATED_PERIOD_ORDER.filter((p) => buckets[p].length > 0).map(
    (period) => ({
      period,
      label: getCreatedPeriodLabel(period),
      leads: buckets[period],
    })
  );
}
